import { ModelDropdown, type ModelDropdownOption } from "@/components/molecules/ModelDropdown/ModelDropdown";
import { FormField } from "@/overlay/components/form-field";
import { t } from "@lingui/core/macro";
import { MmEnsdropdown } from "@mm/zeus-react";
import type React from "react";
import { useCallback, useEffect } from "react";

type SelectionChangedEvent = {
  detail: {
    members: { id: string }[];
    quantiles: { id: string }[];
  };
};

export type ModelEnsSelectorProps = {
  ensOptions: {
    members: string[];
    quantiles: string[];
  };
  modelOptions: ModelDropdownOption[];
  selectedModel?: string;
  selectedEnsMembers?: string[];
  allowMultipleEnsSelect?: boolean;
  onModelChange: (modelId: string) => void;
  onEnsMembersChange: (ensMembers: string[]) => void;
};

export function ModelEnsSelector({
  ensOptions,
  modelOptions,
  selectedModel,
  selectedEnsMembers,
  allowMultipleEnsSelect = true,
  onModelChange,
  onEnsMembersChange,
}: ModelEnsSelectorProps) {
  const ensMembers = ensOptions.members;
  const ensQuantiles = ensOptions.quantiles;

  useEffect(() => {
    if (ensMembers.length === 0 || (selectedEnsMembers?.length ?? 0) > 0) {
      return;
    }

    const defaultMember = ensMembers.includes("mean") ? "mean" : ensMembers[0];
    if (!defaultMember) {
      return;
    }

    onEnsMembersChange([defaultMember]);
  }, [ensMembers, selectedEnsMembers, onEnsMembersChange]);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const { members, quantiles } = event.detail;
      const filteredMembers = members.filter((member) => !ensQuantiles.includes(member.id));
      const nextSelection = [...filteredMembers.map((member) => member.id), ...quantiles.map((q) => q.id)];

      if (nextSelection.length === 0) {
        onEnsMembersChange(["mean"]);
        return;
      }

      if (!allowMultipleEnsSelect) {
        const currentSelection = selectedEnsMembers ?? [];
        const newlyAdded = nextSelection.find((id) => !currentSelection.includes(id));
        onEnsMembersChange([newlyAdded ?? nextSelection[0]]);
        return;
      }

      onEnsMembersChange(nextSelection);
    },
    [allowMultipleEnsSelect, ensQuantiles, onEnsMembersChange, selectedEnsMembers],
  );

  return (
    <>
      <FormField label={t`Source`}>
        <ModelDropdown
          options={modelOptions}
          value={selectedModel}
          placeholder={"Select a model"}
          onChange={onModelChange}
          disabled={modelOptions.length === 1}
        />
      </FormField>

      {ensMembers.length > 0 && (
        <FormField label={t`Ens Members`} noSemanticLinking>
          <MmEnsdropdown
            placeholder={t`Select...`}
            members={ensMembers
              .filter((member) => !ensQuantiles.includes(member))
              .map((member) => ({ id: member, title: member }))}
            quantiles={ensQuantiles.map((quantile) => ({ id: quantile, title: quantile }))}
            selected={allowMultipleEnsSelect ? selectedEnsMembers || [] : (selectedEnsMembers || []).slice(0, 1)}
            onSelectionChanged={handleSelectionChanged}
          />
        </FormField>
      )}
    </>
  );
}
